// component-custom-cart.js - With Store Credit Feature + Send to Designer
(function() {
  'use strict';

  let isUpdating = false;
  
  // Store credit state
  let storeCreditData = null;
  let isStoreCreditApplied = false;
  let isLoadingStoreCredit = false;

  // Get user location from localStorage
  function getUserLocation() {
    const location = localStorage.getItem('userLocation');
    return location ? location.toLowerCase().trim() : 'bengaluru';
  }

  // Get current project number
  function getCurrentProject() {
    try {
      const userProject = localStorage.getItem('userProject');
      if (userProject) {
        const parsed = JSON.parse(userProject);
        if (parsed && parsed.project_no) {
          return String(parsed.project_no);
        }
      }
    } catch (e) {}
    
    if (window.userProject && window.userProject.project_no) {
      return String(window.userProject.project_no);
    }
    
    const currentProject = localStorage.getItem('bonito_current_project');
    if (currentProject) {
      return currentProject;
    }
    
    return null;
  }

  // Format price (last 2 digits are decimal)
  function formatPrice(priceInCents) {
    if (!priceInCents || isNaN(priceInCents)) return '₹0';
    
    const price = priceInCents / 100;
    return '₹' + price.toLocaleString('en-IN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  }

  // Format price from rupees
  function formatPriceRupees(priceInRupees) {
    if (!priceInRupees || isNaN(priceInRupees)) return '₹0';
    
    return '₹' + priceInRupees.toLocaleString('en-IN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  }

  // Get price for user's location
  function getLocationPrice(cityPricingJson, userLocation) {
    if (!cityPricingJson) return null;

    let pricing;
    try {
      pricing = typeof cityPricingJson === 'string' 
        ? JSON.parse(cityPricingJson) 
        : cityPricingJson;
    } catch (e) {
      return null;
    }

    if (pricing[userLocation]) {
      return pricing[userLocation];
    }

    const locationKey = Object.keys(pricing).find(
      key => key.toLowerCase() === userLocation.toLowerCase()
    );
    
    return locationKey ? pricing[locationKey] : null;
  }

  // Get ERP code from cart item element
  function getErpCodeFromItem(itemElement) {
    const erpCodeAttr = itemElement.getAttribute('data-erp-code');
    if (erpCodeAttr && erpCodeAttr !== '' && erpCodeAttr !== 'null') {
      return erpCodeAttr;
    }
    
    if (window.bonitoPersistentCart) {
      const cartItems = document.querySelectorAll('.js-cart-item');
      const index = Array.from(cartItems).indexOf(itemElement);
      
      const localCart = window.bonitoPersistentCart.getLocalCart();
      if (localCart) {
        const erpCodes = Object.keys(localCart);
        if (erpCodes[index]) {
          return erpCodes[index];
        }
      }
    }
    
    const lineItemKey = itemElement.getAttribute('data-line-item-key');
    if (lineItemKey && window.Shopify && window.Shopify.cart) {
      const cartItem = window.Shopify.cart.items.find(item => item.key === lineItemKey);
      if (cartItem) {
        if (cartItem.properties && cartItem.properties._bonito_erp_code) {
          return cartItem.properties._bonito_erp_code;
        }
        if (cartItem.sku) {
          return cartItem.sku;
        }
      }
    }
    
    const skuElement = itemElement.querySelector('[data-sku]');
    if (skuElement) {
      return skuElement.getAttribute('data-sku');
    }
    
    return null;
  }
  
  // Build ERP code mapping
  async function buildCartErpCodeMapping() {
    const mapping = {};
    try {
      const response = await fetch('/cart.js');
      if (response.ok) {
        const cart = await response.json();
        cart.items.forEach((item, index) => {
          let erpCode = null;
          if (item.properties && item.properties._bonito_erp_code) {
            erpCode = item.properties._bonito_erp_code;
          } else if (item.sku) {
            erpCode = item.sku;
          }
          if (erpCode) {
            mapping[index] = erpCode;
          }
        });
      }
    } catch (error) {
      console.error('[Store Credit] Error building ERP mapping:', error);
    }
    return mapping;
  }

  // Fetch store credit preview from API
  async function fetchStoreCreditPreview() {
    const projectNo = getCurrentProject();
    if (!projectNo) {
      console.warn('[Store Credit] No project selected');
      return null;
    }

    try {
      const response = await fetch(`/apps/pulse/redeem-preview?project_no=${projectNo}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        cache: 'no-cache'
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.message || 'API returned unsuccessful');
      }

      console.log('[Store Credit] Preview fetched:', data.data);
      return data.data;

    } catch (error) {
      console.error('[Store Credit] Fetch error:', error);
      throw error;
    }
  }

  // Build Points mapping for display purposes
  function buildPointsMap(storeCreditResponse) {
    const pointsMap = {};
    
    if (storeCreditResponse && storeCreditResponse.wishlist_items) {
      storeCreditResponse.wishlist_items.forEach(item => {
        if (item.erp_code) {
          pointsMap[item.erp_code] = {
            redeemable_points: item.redeemable_points || 0,
            quantity: item.quantity || 1
          };
        }
      });
    }
    
    return pointsMap;
  }

  // Update all prices in the cart
  async function updateCartPrices() {
    if (isUpdating) return;
    isUpdating = true;

    const userLocation = getUserLocation();
    const cartItems = document.querySelectorAll('.js-cart-item');
    
    let cartTotal = 0;
    let totalRedeemablePoints = 0;
    let totalRedeemableCash = 0;

    // 1. Assign Globals directly from API response
    if (isStoreCreditApplied && storeCreditData) {
        // API returns values in rupees, convert to paisa (cents)
        const cashRupees = storeCreditData.total_redeemable_cash || 0;
        totalRedeemableCash = cashRupees * 100;

        const pointsRupees = storeCreditData.total_redeemable_points || 0;
        totalRedeemablePoints = pointsRupees * 100;
    }

    // 2. Build Points Map for Visual Display on Line Items
    const pointsMap = isStoreCreditApplied && storeCreditData 
      ? buildPointsMap(storeCreditData) 
      : {};
    
    const erpMapping = await buildCartErpCodeMapping();

    // 3. Loop through items for Cart Total & Visual Updates
    cartItems.forEach((item, index) => {
      const cityPricingData = item.getAttribute('data-city-pricing');
      if (!cityPricingData || cityPricingData === 'null' || cityPricingData === '') return;

      const quantityInput = item.querySelector('.js-quantity-input');
      const quantity = quantityInput ? parseInt(quantityInput.value) || 1 : 1;
      const locationPrice = getLocationPrice(cityPricingData, userLocation);
      
      if (!locationPrice) return;

      const lineTotal = locationPrice * quantity;
      cartTotal += lineTotal;

      // Identify Item for Points Display
      let erpCode = getErpCodeFromItem(item);
      if (!erpCode && erpMapping[index]) {
        erpCode = erpMapping[index];
      }
      
      // Get points specific to this item (FOR DISPLAY ONLY)
      const pointsInfo = erpCode ? pointsMap[erpCode] : null;
      const itemPointsInRupees = pointsInfo ? pointsInfo.redeemable_points : 0;
      const itemPointsInPaisa = itemPointsInRupees * 100;

      // Update Unit Price UI
      const unitPriceEl = item.querySelector('.js-unit-price');
      if (unitPriceEl) {
        unitPriceEl.textContent = formatPrice(locationPrice);
        unitPriceEl.classList.add('price-loaded');
      }

      // Update Line Total UI (Visual indication of points discount)
      const lineTotalEl = item.querySelector('.js-line-total');
      if (lineTotalEl) {
        if (isStoreCreditApplied && itemPointsInPaisa > 0) {
          const finalLineTotal = lineTotal - itemPointsInPaisa;
          lineTotalEl.innerHTML = `
            <span class="line-original-price" style="text-decoration: line-through; color: #999; font-size: 0.9em;">${formatPrice(lineTotal)}</span>
            <span class="line-credit-discount" style="color: #28a745; font-size: 0.85em;">-${formatPrice(itemPointsInPaisa)}</span>
            <span class="line-final-price" style="font-weight: 600;">${formatPrice(finalLineTotal)}</span>
          `;
        } else {
          lineTotalEl.innerHTML = `<span>${formatPrice(lineTotal)}</span>`;
        }
        lineTotalEl.classList.add('price-loaded');
      }
    });

    // 4. Calculate Grand Total
    const grandTotal = cartTotal - totalRedeemablePoints - totalRedeemableCash;

    // 5. Update Bottom Display
    updateTotalDisplay(cartTotal, totalRedeemablePoints, totalRedeemableCash, grandTotal);

    setTimeout(() => {
      isUpdating = false;
    }, 50);
  }

  // Update the total display section
  function updateTotalDisplay(cartTotal, totalPoints, totalCash, grandTotal) {
    const totalDisplayContainer = document.querySelector('.cart-total-display');
    
    if (totalDisplayContainer) {
      if (isStoreCreditApplied && (totalPoints > 0 || totalCash > 0)) {
        
        let htmlContent = `
          <div class="d-flex between-xs middle-xs mb-s">
            <span class="fs-body-l">Total:</span>
            <span class="fs-body-l fw-500 js-cart-subtotal-price">${formatPrice(cartTotal)}</span>
          </div>
        `;

        // Points Row
        if (totalPoints > 0) {
            htmlContent += `
              <div class="d-flex between-xs middle-xs mb-s" style="color: #28a745;">
                <span class="fs-body-m">Point Applied:</span>
                <span class="fs-body-m fw-500 js-store-credit-amount">-${formatPrice(totalPoints)}</span>
              </div>
            `;
        }

        // Cash Row
        if (totalCash > 0) {
            htmlContent += `
              <div class="d-flex between-xs middle-xs mb-s" style="color: #28a745;">
                <span class="fs-body-m">Cash Applied:</span>
                <span class="fs-body-m fw-500 js-cash-credit-amount">-${formatPrice(totalCash)}</span>
              </div>
            `;
        }

        // Grand Total
        htmlContent += `
          <div class="d-flex between-xs middle-xs pt-s" style="border-top: 2px solid #eee;">
            <span class="fs-heading-m fw-600">Grand Total:</span>
            <span class="fs-heading-l fw-700 js-cart-total-price">${formatPrice(grandTotal)}</span>
          </div>
        `;

        totalDisplayContainer.innerHTML = htmlContent;

      } else {
        totalDisplayContainer.innerHTML = `
          <div class="d-flex between-xs middle-xs">
            <span class="fs-heading-m fw-600">Total:</span>
            <span class="fs-heading-l fw-700 js-cart-total-price">${formatPrice(cartTotal)}</span>
          </div>
        `;
      }
    }
  }

  // Apply store credit
  async function applyStoreCredit() {
    if (isLoadingStoreCredit) return;
    
    const button = document.querySelector('.js-apply-store-credit-btn');
    
    try {
      isLoadingStoreCredit = true;
      
      if (button) {
        button.disabled = true;
        button.innerHTML = `
          <svg class="spinner" width="20" height="20" viewBox="0 0 50 50" style="animation: spin 1s linear infinite; margin-right: 8px;">
            <circle cx="25" cy="25" r="20" fill="none" stroke="currentColor" stroke-width="3" stroke-dasharray="100, 150" stroke-linecap="round"/>
          </svg>
          <span>Applying...</span>
        `;
      }

      storeCreditData = await fetchStoreCreditPreview();
      
      const totalPoints = storeCreditData?.total_redeemable_points || 0;
      const totalCash = storeCreditData?.total_redeemable_cash || 0;

      
      const totalValueAvailable = totalPoints + totalCash;
      
      if (storeCreditData && totalValueAvailable > 0) {
        isStoreCreditApplied = true;
        
        await updateCartPrices();
        
        if (button) {
          button.innerHTML = `
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="margin-right: 8px;">
              <path d="M5 13l4 4L19 7"/>
            </svg>
            <span>Credits Applied</span>
          `;
          button.classList.remove('btn-apply-credit');
          button.classList.add('btn-remove-credit');
          button.disabled = false;
        }
        
        const formattedTotal = formatPriceRupees(totalValueAvailable);
        showNotification(`The amount of ${formattedTotal} has been applied successfully!`, 'success');
        
      } else {
        showNotification('No store credits or cash available for this order.', 'info');
        
        if (button) {
          button.innerHTML = `
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="margin-right: 8px;">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            <span>No Credits Available</span>
          `;
          button.disabled = true;
          
          setTimeout(() => {
            resetStoreCreditButton();
          }, 3000);
        }
      }

    } catch (error) {
      console.error('[Store Credit] Apply error:', error);
      showNotification('Failed to apply store credits. Please try again.', 'error');
      resetStoreCreditButton();
      
    } finally {
      isLoadingStoreCredit = false;
    }
  }

  // Reset store credit button
  function resetStoreCreditButton() {
    const button = document.querySelector('.js-apply-store-credit-btn');
    if (button) {
      button.innerHTML = `
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="margin-right: 8px;">
          <circle cx="12" cy="12" r="10"/>
          <path d="M9 12l2 2 4-4"/>
        </svg>
        <span>Apply Store Credits</span>
      `;
      button.classList.remove('btn-remove-credit');
      button.classList.add('btn-apply-credit');
      button.disabled = false;
    }
  }

  // Remove store credit
  function removeStoreCredit() {
    isStoreCreditApplied = false;
    storeCreditData = null;
    updateCartPrices();
    resetStoreCreditButton();
    showNotification('Store credits removed.', 'info');
  }

  // Handle store credit button click
  function handleStoreCreditClick(e) {
    e.preventDefault();
    const button = e.currentTarget;
    if (button.classList.contains('btn-remove-credit') || isStoreCreditApplied) {
      removeStoreCredit();
    } else {
      applyStoreCredit();
    }
  }

  // Send to Designer functionality
  async function sendToDesigner() {
    const projectNo = getCurrentProject();
    
    if (!projectNo) {
      showDialog('error', 'No Project Selected', 'Please select a project before sending to stylist.');
      return;
    }

    const button = document.querySelector('.js-send-to-designer-btn');
    
    try {
      if (button) {
        button.disabled = true;
        button.innerHTML = `
          <svg class="spinner" width="20" height="20" viewBox="0 0 50 50" style="animation: spin 1s linear infinite; margin-right: 8px;">
            <circle cx="25" cy="25" r="20" fill="none" stroke="currentColor" stroke-width="3" stroke-dasharray="100, 150" stroke-linecap="round"/>
          </svg>
          <span>Sending...</span>
        `;
      }

      const response = await fetch(`/apps/pulse/orders?project_no=${projectNo}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        showDialog('success', 'Success!', 'Cart items are added to draft quote and your stylist will check this.');
        
        if (button) {
          button.innerHTML = `
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="margin-right: 8px;">
              <path d="M5 13l4 4L19 7"/>
            </svg>
            <span>Sent to Stylist</span>
          `;
          
          setTimeout(() => {
            button.innerHTML = `
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="margin-right: 8px;">
                <line x1="22" y1="2" x2="11" y2="13"/>
                <polygon points="22 2 15 22 11 13 2 9 22 2"/>
              </svg>
              <span>Send to Stylist</span>
            `;
            button.disabled = false;
          }, 3000);
        }
      } else {
        throw new Error(data.message || 'Failed to send to stylist');
      }

    } catch (error) {
      console.error('[Send to Stylist] Error:', error);
      showDialog('error', 'Failed', 'Failed to share with stylist. Please try again.');
      
      if (button) {
        button.innerHTML = `
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="margin-right: 8px;">
            <line x1="22" y1="2" x2="11" y2="13"/>
            <polygon points="22 2 15 22 11 13 2 9 22 2"/>
          </svg>
          <span>Send to Stylist</span>
        `;
        button.disabled = false;
      }
    }
  }

  // Handle send to designer button click
  function handleSendToDesignerClick(e) {
    e.preventDefault();
    sendToDesigner();
  }

  // Show notification
  function showNotification(message, type = 'info') {
    const existing = document.querySelectorAll('.store-credit-notification');
    existing.forEach(el => el.remove());
    
    const colors = {
      success: { bg: '#d4edda', border: '#c3e6cb', text: '#155724' },
      error: { bg: '#f8d7da', border: '#f5c6cb', text: '#721c24' },
      info: { bg: '#d1ecf1', border: '#bee5eb', text: '#0c5460' }
    };
    
    const color = colors[type] || colors.info;
    
    const html = `
      <div class="store-credit-notification" style="
        position: fixed;
        top: 20px;
        right: 20px;
        background: ${color.bg};
        color: ${color.text};
        padding: 0.75rem 1.25rem;
        border-radius: 8px;
        border: 1px solid ${color.border};
        z-index: 9999;
        max-width: 350px;
        box-shadow: 0 2px 10px rgba(0,0,0,0.1);
        animation: slideInRight 0.3s ease-out;
      ">
        <div style="display: flex; align-items: center; gap: 10px;">
          <span>${message}</span>
          <button onclick="this.parentElement.parentElement.remove()" style="
            background: none;
            border: none;
            cursor: pointer;
            font-size: 1.25rem;
            opacity: 0.7;
            padding: 0;
            line-height: 1;
          ">×</button>
        </div>
      </div>
      <style>
        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      </style>
    `;
    
    document.body.insertAdjacentHTML('beforeend', html);
    setTimeout(() => {
      const el = document.querySelector('.store-credit-notification');
      if (el) el.remove();
    }, 4000);
  }

  // Show dialog
  function showDialog(type, title, message) {
    const existing = document.querySelectorAll('.designer-dialog-overlay');
    existing.forEach(el => el.remove());
    
    const colors = {
      success: { icon: '#28a745', button: '#28a745' },
      error: { icon: '#dc3545', button: '#dc3545' }
    };
    
    const color = colors[type] || colors.success;
    
    const iconSvg = type === 'success' 
      ? `<svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="${color.icon}" stroke-width="2">
           <circle cx="12" cy="12" r="10"/>
           <path d="M9 12l2 2 4-4"/>
         </svg>`
      : `<svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="${color.icon}" stroke-width="2">
           <circle cx="12" cy="12" r="10"/>
           <line x1="12" y1="8" x2="12" y2="12"/>
           <line x1="12" y1="16" x2="12.01" y2="16"/>
         </svg>`;
    
    const html = `
      <div class="designer-dialog-overlay" style="
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.5);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 10000;
        animation: fadeIn 0.2s ease-out;
      ">
        <div class="designer-dialog" style="
          background: white;
          border-radius: 12px;
          padding: 2rem;
          max-width: 400px;
          width: 90%;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
          text-align: center;
          animation: slideUp 0.3s ease-out;
        ">
          <div style="margin-bottom: 1.5rem;">
            ${iconSvg}
          </div>
          <h3 style="margin: 0 0 1rem 0; font-size: 1.5rem; color: #333;">${title}</h3>
          <p style="margin: 0 0 1.5rem 0; color: #666; font-size: 1rem; line-height: 1.5;">${message}</p>
          <button class="dialog-close-btn" style="
            background: ${color.button};
            color: white;
            border: none;
            padding: 0.75rem 2rem;
            border-radius: 6px;
            font-size: 1rem;
            cursor: pointer;
            font-weight: 600;
            transition: opacity 0.2s;
          " onmouseover="this.style.opacity='0.9'" onmouseout="this.style.opacity='1'">
            OK
          </button>
        </div>
      </div>
      <style>
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      </style>
    `;
    
    document.body.insertAdjacentHTML('beforeend', html);
    
    const closeBtn = document.querySelector('.dialog-close-btn');
    const overlay = document.querySelector('.designer-dialog-overlay');
    
    closeBtn.addEventListener('click', () => overlay.remove());
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) overlay.remove();
    });
  }

  // Initialize store credit button
  function initStoreCreditButton() {
    const button = document.querySelector('.js-apply-store-credit-btn');
    if (button) {
      button.removeEventListener('click', handleStoreCreditClick);
      button.addEventListener('click', handleStoreCreditClick);
      console.log('[Store Credit] Button initialized');
    }
  }

  // Create Send to Designer button only
  function createSendToDesignerButton() {
    // Look for the cart total display container
    const totalDisplayContainer = document.querySelector('.cart-total-display');
    
    if (!totalDisplayContainer) {
      console.warn('[Send to Stylist] Total display container not found');
      return;
    }

    // Check if Send to Designer button already exists
    if (document.querySelector('.js-send-to-designer-btn')) {
      console.log('[Send to Stylist] Button already exists');
      return;
    }

    // Create Send to Designer button
    const designerBtn = document.createElement('button');
    designerBtn.className = 'js-send-to-designer-btn';
    designerBtn.style.cssText = `
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      padding: 1rem;
      background: #007bff;
      color: white;
      border: none;
      border-radius: 8px;
      font-size: 1rem;
      font-weight: 600;
      cursor: pointer;
      transition: background 0.2s;
      margin-top: 1rem;
    `;
    designerBtn.innerHTML = `
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="margin-right: 8px;">
        <line x1="22" y1="2" x2="11" y2="13"/>
        <polygon points="22 2 15 22 11 13 2 9 22 2"/>
      </svg>
      <span>Send to Stylist</span>
    `;
    designerBtn.onmouseover = function() { this.style.background = '#0056b3'; };
    designerBtn.onmouseout = function() { this.style.background = '#007bff'; };

    // Insert after the total display container
    totalDisplayContainer.parentNode.insertBefore(designerBtn, totalDisplayContainer.nextSibling);

    console.log('[Send to Stylist] Button created and added to DOM');
  }

  // Initialize send to designer button
  function initSendToDesignerButton() {
    const button = document.querySelector('.js-send-to-designer-btn');
    if (button) {
      button.removeEventListener('click', handleSendToDesignerClick);
      button.addEventListener('click', handleSendToDesignerClick);
      console.log('[Send to Stylist] Button initialized');
    }
  }

  // Hook into the original SHTCartForm class
  function hookIntoCartForm() {
    if (!customElements.get('sht-cart-frm')) {
      setTimeout(hookIntoCartForm, 100);
      return;
    }

    const cartForm = document.querySelector('sht-cart-frm');
    if (cartForm && cartForm.updateQuantity) {
      const originalUpdateQuantity = cartForm.updateQuantity.bind(cartForm);
      
      cartForm.updateQuantity = function(index, quantity, button) {
        const result = originalUpdateQuantity(index, quantity, button);
        if (isStoreCreditApplied) {
          console.log('[Store Credit] Quantity changed, resetting credit');
          isStoreCreditApplied = false;
          storeCreditData = null;
          resetStoreCreditButton();
        }
        setTimeout(() => {
          updateCartPrices();
        }, 100);
        return result;
      };
    }
  }

  // Intercept fetch for cart changes
  function interceptFetch() {
    const originalFetch = window.fetch;
    window.fetch = function(...args) {
      const url = args[0];
      return originalFetch.apply(this, args).then(response => {
        if (typeof url === 'string' && url.includes('/cart/change')) {
          if (isStoreCreditApplied) {
            isStoreCreditApplied = false;
            storeCreditData = null;
            resetStoreCreditButton();
          }
          const clonedResponse = response.clone();
          clonedResponse.text().then(() => {
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                updateCartPrices();
              });
            });
          });
        }
        return response;
      });
    };
  }

  // Setup event listeners
  function setupListeners() {
    window.addEventListener('storage', function(e) {
      if (e.key === 'userLocation') {
        if (isStoreCreditApplied) {
          isStoreCreditApplied = false;
          storeCreditData = null;
          resetStoreCreditButton();
        }
        updateCartPrices();
      }
    });

    window.addEventListener('projectLocationChanged', function(e) {
      if (isStoreCreditApplied) {
        isStoreCreditApplied = false;
        storeCreditData = null;
        resetStoreCreditButton();
      }
      setTimeout(updateCartPrices, 100);
    });

    window.addEventListener('locationSelected', function(e) {
      if (isStoreCreditApplied) {
        isStoreCreditApplied = false;
        storeCreditData = null;
        resetStoreCreditButton();
      }
      setTimeout(updateCartPrices, 100);
    });

    document.addEventListener('visibilitychange', function() {
      if (!document.hidden) {
        setTimeout(updateCartPrices, 100);
      }
    });

    document.addEventListener('cart:refresh', function() {
      if (isStoreCreditApplied) {
        isStoreCreditApplied = false;
        storeCreditData = null;
        resetStoreCreditButton();
      }
      setTimeout(updateCartPrices, 200);
    });

    document.addEventListener('cart:updated', function() {
      if (isStoreCreditApplied) {
        isStoreCreditApplied = false;
        storeCreditData = null;
        resetStoreCreditButton();
      }
      setTimeout(updateCartPrices, 200);
    });

    // Show project-specific buttons when project data arrives
    window.addEventListener('projectsLoaded', function() {
      setTimeout(updateProjectButtons, 100);
    });

    // Hide project-specific buttons when confirmed no project
    window.addEventListener('noProjectsFound', function() {
      updateProjectButtons();
    });

    window.addEventListener('customerDataLoaded', function() {
      setTimeout(updateProjectButtons, 100);
    });

    window.addEventListener('customerDataLoadFailed', function() {
      updateProjectButtons();
    });
  }

  // Show/hide project-specific buttons based on whether user has a PID
  function updateProjectButtons() {
    const projectNo = getCurrentProject();

    const storeCreditSection = document.querySelector('.store-credit-section');
    if (storeCreditSection) {
      storeCreditSection.style.display = projectNo ? '' : 'none';
    }

    const designerBtn = document.querySelector('.js-send-to-designer-btn');
    if (designerBtn) {
      designerBtn.style.display = projectNo ? '' : 'none';
    }

    const notice = document.getElementById('cartNotice');
    const noticeText = document.getElementById('cartNoticeText');
    if (notice && noticeText) {
      if (projectNo) {
        notice.style.display = 'none';
      } else {
        const isLoggedIn = notice.dataset.loggedIn === 'true';
        noticeText.textContent = isLoggedIn
          ? 'Cart will not save as there is no linked project found.'
          : 'Please login to persist cart.';
        notice.style.display = '';
      }
    }
  }

  // Initialize
  function init() {
    createSendToDesignerButton();
    updateProjectButtons();
    setTimeout(updateCartPrices, 300);
    setupListeners();
    interceptFetch();
    hookIntoCartForm();
    initStoreCreditButton();
    initSendToDesignerButton();
    setTimeout(updateCartPrices, 1500);
    setTimeout(initStoreCreditButton, 500);
    setTimeout(initSendToDesignerButton, 500);
    setTimeout(createSendToDesignerButton, 600);
    setTimeout(updateProjectButtons, 700);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.updateCartPrices = function() {
    isUpdating = false;
    updateCartPrices();
  };
  window.getUserLocation = getUserLocation;
  window.applyStoreCredit = applyStoreCredit;
  window.removeStoreCredit = removeStoreCredit;
  window.isStoreCreditApplied = function() { return isStoreCreditApplied; };
  window.sendToDesigner = sendToDesigner;

})();
